import { SupabaseClient } from "@supabase/supabase-js";
import { logException, SentryFeatureTags } from "@punchup/sentry";
import { Database } from "../../supabase.types";
import type {
  SupportCaseInsert,
  SupportCaseListCursor,
  SupportCaseListRow,
  SupportCaseMediaInsert,
  SupportCaseMediaRow,
  SupportCaseSearchHit,
  SupportCaseStatus,
  SupportCaseUpdate,
  SupportCaseWithMediaRows,
  SupportSearchMatchKind,
  SupportVerificationStatus,
} from "../../models/support";

/**
 * Queries for the support-case knowledge base. Called with the service-role
 * client from the super-admin-guarded /api/v2/internal/support routes; these
 * functions return rows, and @punchup/support/server turns them into DTOs.
 */

type Client = SupabaseClient<Database>;

function fail(
  action: string,
  error: unknown,
  extra?: Record<string, unknown>
): never {
  logException(error, {
    tags: { feature: SentryFeatureTags.SupportAssistant, action },
    extra,
  });
  throw error;
}

function withSortedMedia<T extends { media: SupportCaseMediaRow[] }>(
  row: T
): T {
  return {
    ...row,
    media: [...row.media].sort((a, b) => a.position - b.position),
  };
}

const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The newest-first case list, keyset-paginated on (updated_at, id). Archived
 * cases only appear when asked for by status.
 */
export async function listSupportCases(
  supabase: Client,
  options: {
    limit: number;
    status?: SupportCaseStatus;
    category?: string;
    cursor?: SupportCaseListCursor;
  }
): Promise<{ rows: SupportCaseListRow[]; hasMore: boolean }> {
  let query = supabase
    .from("support_cases")
    .select(
      "id, title, category, tags, status, verified_at, content_updated_at, updated_at, support_case_media(count)"
    )
    .order("updated_at", { ascending: false })
    .order("id", { ascending: false })
    .limit(options.limit + 1);

  query = options.status
    ? query.eq("status", options.status)
    : query.neq("status", "archived");

  if (options.category) query = query.eq("category", options.category);

  if (options.cursor) {
    const { updatedAt, id } = options.cursor;
    // The cursor comes back from the browser and is spliced into a PostgREST
    // filter string, so only exact timestamp/uuid shapes get through.
    if (!ISO_TIMESTAMP.test(updatedAt) || !UUID.test(id)) {
      throw new Error("Invalid support case list cursor");
    }
    query = query.or(
      `updated_at.lt."${updatedAt}",and(updated_at.eq."${updatedAt}",id.lt.${id})`
    );
  }

  const { data, error } = await query;
  if (error) fail("listSupportCases", error);

  const rows: SupportCaseListRow[] = (data ?? []).map(
    ({ support_case_media, ...row }) => ({
      ...row,
      media_count: support_case_media?.[0]?.count ?? 0,
    })
  );

  return {
    rows: rows.slice(0, options.limit),
    hasMore: rows.length > options.limit,
  };
}

export async function getSupportCase(
  supabase: Client,
  caseId: string
): Promise<SupportCaseWithMediaRows | null> {
  const { data, error } = await supabase
    .from("support_cases")
    .select("*, media:support_case_media(*)")
    .eq("id", caseId)
    .maybeSingle();

  if (error) fail("getSupportCase", error, { caseId });
  return data ? withSortedMedia(data) : null;
}

/** Cases with media for a set of ids, in the order the ids were given. */
export async function getSupportCasesByIds(
  supabase: Client,
  caseIds: string[]
): Promise<SupportCaseWithMediaRows[]> {
  if (caseIds.length === 0) return [];

  const { data, error } = await supabase
    .from("support_cases")
    .select("*, media:support_case_media(*)")
    .in("id", caseIds);

  if (error) fail("getSupportCasesByIds", error, { caseIds });

  const byId = new Map((data ?? []).map((row) => [row.id, row]));
  return caseIds
    .map((id) => byId.get(id))
    .filter((row): row is NonNullable<typeof row> => row !== undefined)
    .map(withSortedMedia);
}

/** Ranked full-text search (see the `search_support_cases` RPC). */
export async function searchSupportCases(
  supabase: Client,
  params: {
    query: string;
    limit: number;
    staleAfterDays: number;
    category?: string;
    statuses?: SupportCaseStatus[];
  }
): Promise<SupportCaseSearchHit[]> {
  const { data, error } = await supabase.rpc("search_support_cases", {
    p_query: params.query,
    p_limit: params.limit,
    p_category: params.category,
    p_statuses: params.statuses ?? ["published"],
    p_stale_after_days: params.staleAfterDays,
  });

  if (error) fail("searchSupportCases", error);

  return (data ?? []).map((hit) => ({
    caseId: hit.case_id,
    rank: hit.rank,
    matchKind: hit.match_kind as SupportSearchMatchKind,
    verification: hit.verification_status as SupportVerificationStatus,
  }));
}

/**
 * Creates a case and its media. If the media cannot be attached the case is
 * deleted again: a case must never publish without the screenshots its
 * solution points at.
 */
export async function createSupportCase(
  supabase: Client,
  input: {
    caseRow: SupportCaseInsert;
    media: Omit<SupportCaseMediaInsert, "case_id">[];
  }
): Promise<string> {
  const { data, error } = await supabase
    .from("support_cases")
    .insert(input.caseRow)
    .select("id")
    .single();

  if (error || !data) {
    fail("createSupportCase", error ?? new Error("No case returned"));
  }

  if (input.media.length > 0) {
    const { error: mediaError } = await supabase
      .from("support_case_media")
      .insert(input.media.map((item) => ({ ...item, case_id: data.id })));

    if (mediaError) {
      await supabase.from("support_cases").delete().eq("id", data.id);
      fail("createSupportCase.media", mediaError, { caseId: data.id });
    }
  }

  return data.id;
}

export type SupportCaseMediaEdit = {
  /** Existing items to keep, with their (possibly edited) description and new position. */
  keep: Pick<
    SupportCaseMediaRow,
    "id" | "description" | "description_source" | "position"
  >[];
  /** New items to attach. */
  add: Omit<SupportCaseMediaInsert, "case_id">[];
};

/**
 * Updates a case. When `media` is given it is the complete new media list:
 * existing items not in `keep` are deleted, and their storage paths are
 * returned so the caller can remove the objects. Only rows whose values
 * actually change are written, so re-saving an untouched case does not count
 * as an edit (which would reset its verification).
 */
export async function updateSupportCase(
  supabase: Client,
  caseId: string,
  input: { patch: SupportCaseUpdate; media?: SupportCaseMediaEdit }
): Promise<{ found: boolean; removedStoragePaths: string[] }> {
  const existingCase = await getSupportCase(supabase, caseId);
  if (!existingCase) return { found: false, removedStoragePaths: [] };

  if (Object.keys(input.patch).length > 0) {
    const { error } = await supabase
      .from("support_cases")
      .update(input.patch)
      .eq("id", caseId);
    if (error) fail("updateSupportCase", error, { caseId });
  }

  if (!input.media) return { found: true, removedStoragePaths: [] };

  // Not a transaction: each write is its own request. In this order a failure
  // leaves only part of the edit applied, never anything else, and saving the
  // same edit again finishes it (dropped rows are already gone, kept rows are
  // re-applied, new rows go in in one insert). A failed save is retried, not
  // repaired.
  const keepById = new Map(input.media.keep.map((item) => [item.id, item]));
  const removed = existingCase.media.filter((item) => !keepById.has(item.id));

  if (removed.length > 0) {
    const { error } = await supabase
      .from("support_case_media")
      .delete()
      .eq("case_id", caseId)
      .in(
        "id",
        removed.map((item) => item.id)
      );
    if (error) fail("updateSupportCase.removeMedia", error, { caseId });
  }

  for (const current of existingCase.media) {
    const next = keepById.get(current.id);
    if (
      !next ||
      (next.description === current.description &&
        next.description_source === current.description_source &&
        next.position === current.position)
    ) {
      continue;
    }
    const { error } = await supabase
      .from("support_case_media")
      .update({
        description: next.description,
        description_source: next.description_source,
        position: next.position,
      })
      .eq("case_id", caseId)
      .eq("id", current.id);
    if (error) fail("updateSupportCase.updateMedia", error, { caseId });
  }

  if (input.media.add.length > 0) {
    const { error } = await supabase
      .from("support_case_media")
      .insert(input.media.add.map((item) => ({ ...item, case_id: caseId })));
    if (error) fail("updateSupportCase.addMedia", error, { caseId });
  }

  return {
    found: true,
    removedStoragePaths: removed
      .map((item) => item.storage_path)
      .filter((path): path is string => path !== null),
  };
}

/** Stamps (or clears) who vouched that the case's current content still works. */
export async function setSupportCaseVerification(
  supabase: Client,
  caseId: string,
  verification: { verifiedBy: string; verifiedAt: string } | null
): Promise<boolean> {
  const { data, error } = await supabase
    .from("support_cases")
    .update({
      verified_by: verification?.verifiedBy ?? null,
      verified_at: verification?.verifiedAt ?? null,
    })
    .eq("id", caseId)
    .select("id");

  if (error) fail("setSupportCaseVerification", error, { caseId });
  return (data ?? []).length > 0;
}
